import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { tokenStore } from "@/lib/tokenStore";
import { ROLE_ROUTES } from "@/lib/roleRoutes";

const AuthContext = createContext(null);

// iter-55: per-tab login URL memory so logout returns to the exact
// tenant-scoped login URL this tab was opened on. sessionStorage is
// per-tab which is exactly what we want here.
const LOGIN_URL_KEY = "cera_login_url";

function rememberLoginUrl(user) {
  try {
    const path = customLoginPathForUser(user);
    if (path) sessionStorage.setItem(LOGIN_URL_KEY, path);
    else sessionStorage.removeItem(LOGIN_URL_KEY);
  } catch { /* noop */ }
}

function storedLoginPath() {
  try {
    const v = sessionStorage.getItem(LOGIN_URL_KEY);
    return v && v.startsWith("/") ? v : null;
  } catch { return null; }
}

/**
 * Given a user + tenant returned by /auth/login or /auth/me, computes the
 * full custom login URL the user is supposed to use.
 * SUPER_ADMIN users don't have a custom URL — they legitimately use /login.
 */
export function customLoginPathForUser(user) {
  if (!user || user.role === "SUPER_ADMIN") return null;
  const route = ROLE_ROUTES[user.role];
  if (!user.tenant_slug || !route) return null;
  return `/${user.tenant_slug}/${route.slug}/login`;
}

export function customLoginUrlForUser(user) {
  const path = customLoginPathForUser(user);
  if (!path) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return origin + path;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(null);
      setTenant(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch {
      tokenStore.clear();
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /**
   * @param {{ enforceSlug?: string, enforceRole?: string }} ctx
   *   When set, the login is expected to happen at that tenant-scoped URL.
   *   Any mismatch (or a tenant user landing on the generic /login) returns
   *   { ok:false, wrong_url:true, user:{...}, custom_url:"https://..." } WITHOUT
   *   storing a token or setting the auth state.
   */
  const login = async (email, password, ctx = {}) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_mfa) {
        return { ok: true, requires_mfa: true, mfa_token: data.mfa_token, email: data.email };
      }
      const userDoc = data.user;

      // === iter-42 URL gate ===
      // A tenant user must sign in at their /<slug>/<role>/login URL. If the caller
      // did not opt-in to enforcement (generic /login) OR the URL doesn't match,
      // refuse to establish a session and hand back the correct URL to the UI.
      //
      // iter-45: ctx.bypass === true → caller explicitly opted OUT (demo autofill on /login).
      if (userDoc && userDoc.role !== "SUPER_ADMIN" && !ctx.bypass) {
        const expectedPath = customLoginPathForUser(userDoc);
        const genericContext = !ctx.enforceSlug || !ctx.enforceRole;
        const slugMismatch = ctx.enforceSlug && ctx.enforceSlug !== userDoc.tenant_slug;
        const roleMismatch = ctx.enforceRole && ctx.enforceRole !== userDoc.role;
        if (expectedPath && (genericContext || slugMismatch || roleMismatch)) {
          const origin = typeof window !== "undefined" ? window.location.origin : "";
          return {
            ok: false,
            wrong_url: true,
            user: userDoc,
            custom_url: origin + expectedPath,
            error: "Please sign in via your customized portal URL below.",
          };
        }
      }

      if (data.token) tokenStore.set(data.token);
      rememberLoginUrl(userDoc);
      setUser(userDoc);
      setTenant(data.tenant);
      return { ok: true, user: userDoc };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyMfaLogin = async (mfa_token, code) => {
    try {
      const { data } = await api.post("/auth/mfa/login", { mfa_token, code });
      if (data.token) tokenStore.set(data.token);
      rememberLoginUrl(data.user);
      setUser(data.user);
      setTenant(data.tenant);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    // Return this tab's original login URL so the caller can navigate back to it.
    const path = storedLoginPath() || customLoginPathForUser(user) || "/login";
    tokenStore.clear();
    try { sessionStorage.removeItem(LOGIN_URL_KEY); } catch { /* noop */ }
    setUser(null);
    setTenant(null);
    return path;
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, logout, refresh, setTenant, verifyMfaLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
